import React, { useEffect, useState, useCallback, useRef } from "react";
import { useWorkplace } from "../context/WorkplaceContext";
import SvgIcons from "../layouts/SvgIcons";
import apiClient from "../apiClient";
import IonIcon from "../components/IonIcon";
import Modal from "react-modal";

const fmtTime = (s) => {
  if (!isFinite(s) || s <= 0) return "0:00";
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${m}:${String(ss).padStart(2, "0")}`;
};

const Event = () => {
  const [voiceDuration, setVoiceDuration] = useState("0:00");
  const { selectedWorkplaceId } = useWorkplace(); // Context 사용
  const [selectedEvent, setSelectedEvent] = useState(null); // 미디어 열에 있는 문구 선택한 row
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  // 필터/검색
  const [deviceType, setDeviceType] = useState("all"); // 첫번째 dropdownlist
  const [subType, setSubType] = useState("all"); // 두번째 dropdownlist

  const [startDate, setStartDate] = useState(""); // 시작 날짜
  const [endDate, setEndDate] = useState(""); // 종료 날짜

  const [deviceOptions, setDeviceOptions] = useState([
    { value: "all", label: "전체" },
  ]);

  const [subOptions, setSubOptions] = useState([
    { value: "all", label: "전체" },
  ]);

  const [subLoading, setSubLoading] = useState(false);
  const [subError, setSubError] = useState(null);

  // 데이터/페이지네이션
  const [page, setPage] = useState(1);
  const [eventRows, setEventRows] = useState([]); // 리스트에 바인딩할 데이터
  const [eventTotal, setEventTotal] = useState(0); // 총 건수

  const [loading, setLoading] = useState(false);

  const getSaveLabel = (t) => {
    const k = String(t ?? "")
      .trim()
      .toLowerCase();
    if (k.includes("voice")) return "음성저장";
    if (k.includes("video")) return "영상저장";
    return "저장";
  };

  const MEDIA_META = {
    voice: { label: "음성듣기", icon: "IoMic", color: "#15E041" },
    video: { label: "영상보기", icon: "IoVideocam", color: "#15E041" },
    // 필요 시 계속 추가...
  };

  const getMediaMeta = (t) => {
    const key = String(t ?? "")
      .trim()
      .toLowerCase();
    return (
      MEDIA_META[key] || {
        label: "보기",
        icon: "IoOpenOutline",
        color: "#64748B",
      }
    );
  };

  // ▼ 장비종류(DeviceType) 옵션: eventDeviceType만 사용해서 value/label 동일 세팅
  useEffect(() => {
    if (!selectedWorkplaceId || !startDate || !endDate) return;
    let alive = true;

    (async () => {
      try {
        const payload = {
          Page: 1,
          PageSize: 1000,
          OrderType: "eventdevicetype_asc",
          WorkplaceId: selectedWorkplaceId,
          LoginUserId: localStorage.getItem("userid"),
        };
        const { data } = await apiClient.post(
          "/api/Event/all-event-device-type-list",
          payload
        );
        const res = typeof data === "string" ? JSON.parse(data) : data;

        console.log(res);
        const list = res?.data ?? res?.list ?? [];

        const types = Array.from(
          new Set(
            list
              .map((x) => (typeof x === "string" ? x : x?.eventDeviceType))
              .map((v) => String(v ?? "").trim())
              .filter(Boolean)
          )
        );

        const options = [
          { value: "all", label: "전체" },
          ...types.map((v) => ({ value: v, label: v })),
        ];

        if (alive) setDeviceOptions(options);
      } catch (e) {
        console.error("device type list error", e);
        if (alive) setDeviceOptions([{ value: "all", label: "전체" }]);
      }
    })();

    return () => {
      alive = false;
    };
  }, [selectedWorkplaceId, startDate, endDate]);

  useEffect(() => {
    if (!selectedWorkplaceId || !startDate || !endDate) return;
    let alive = true;

    if (deviceType === "all") {
      setSubLoading(false);
      setSubError(null);
      setSubOptions([{ value: "all", label: "전체" }]);
      setSubType("all"); // 안전 리셋
      return;
    }

    setSubLoading(true);
    setSubError(null);

    (async () => {
      try {
        const payload = {
          Page: 1,
          PageSize: 1000,
          OrderType: "eventdevicename_asc",
          WorkplaceId: selectedWorkplaceId,
          LoginUserId: localStorage.getItem("userid"),
          EventType: deviceType === "all" ? "all" : deviceType, // all이면 전체 조회
        };
        const { data } = await apiClient.post(
          "/api/Event/event-event-device-name-list",
          payload
        );
        const res = typeof data === "string" ? JSON.parse(data) : data;
        console.log(res);
        const list = res?.data ?? res?.list ?? [];

        const options = [
          { value: "all", label: "전체" },
          ...Array.from(
            new Set(
              list
                .map((x) => (typeof x === "string" ? x : x?.eventDeviceName))
                .map((v) => String(v ?? "").trim())
                .filter(Boolean)
            )
          ).map((v) => ({ value: v, label: v })),
        ];

        if (alive) {
          setSubOptions(options);
          // 현재 선택이 목록에 없으면 'all'로 리셋 (안전장치)
          // if (!options.some(o => o.value === subType)) setSubType("all");
        }
      } catch (e) {
        console.error("sub type list error", e);
        if (alive) {
          setSubOptions([{ value: "all", label: "전체" }]);
          setSubError("불러오기 실패");
        }
      } finally {
        if (alive) setSubLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [selectedWorkplaceId, startDate, endDate, deviceType]);

  useEffect(() => {
    setSubType("all");
  }, [deviceType]);

  useEffect(() => {
    if (!selectedWorkplaceId) return;
    const today = new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"

    setStartDate(today);
    setEndDate(today);
  }, [selectedWorkplaceId]);

  useEffect(() => {
    if (!selectedWorkplaceId || !startDate || !endDate) return;

    const fetchAll = async () => {
      // 이벤트 로그 리스트 호출
      try {
        setLoading(true);
        const payload = {
          Page: 1,
          PageSize: 1000,
          OrderType: "eventdtm_desc",
          LoginUserId: localStorage.getItem("userid"),
          WorkplaceId: selectedWorkplaceId,
          EventType: deviceType,
          EventName: subType,
          StartDate: startDate,
          EndDate: endDate,
        };

        const { data } = await apiClient.post(
          "/api/Event/all-events-list",
          payload
        );
        const res = typeof data === "string" ? JSON.parse(data) : data;
        console.log(res.data[0]);

        // EventId, WorkplaceId, EventDeviceType, EventDeviceName
        // EventContent, AreaName, EventTime, DownloadType, DownloadDataPath

        const rows = (res?.data ?? []).map((it) => ({
          eventId: it.eventId,
          workplaceId: it.workplaceId,
          workplaceName: it.workplaceName,
          eventDeviceType: it.eventDeviceType,
          eventDeviceName: it.eventDeviceName,
          eventContent: it.eventContent,
          areaName: it.areaName,
          eventTime: it.eventTime,
          downloadType: it.downloadType,
          downloadDataPath: it.downloadDataPath,
          beApiUrl: it.beApiUrl,
        }));

        setEventRows(rows);
        setEventTotal(res?.meta?.totalCount ?? rows.length);
      } catch (e) {
        console.error("events get list error", e);
        setEventRows([]);
        setEventTotal(0);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [selectedWorkplaceId, deviceType, subType, startDate, endDate]);

  const openPopup = (event) => {
    setSelectedEvent(event);
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  // 타입 정규화
  const normType = (t) =>
    String(t ?? "")
      .trim()
      .toLowerCase();

  // 절대 URL 만들기: downloadDataPath가 절대/상대 둘 다 올 수 있으니 안전하게
  const toAbsoluteUrl = (base, path) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    const root = String(base || "").replace(/\/+$/, "");
    const rel = String(path || "").replace(/^\/+/, "");
    return root ? `${root}/${rel}` : `/${rel}`;
  };

  // 미디어 URL 추출(프로젝트/서버 스펙에 맞게 필요한 경우 보완)
  const getVoiceUrl = (ev) => toAbsoluteUrl(ev.beApiUrl, ev.downloadDataPath);
  //const getVideoUrl = (ev) => toAbsoluteUrl(ev.beApiUrl, ev.downloadDataPath);

  return (
    <div className="right">
      <div className="searchbox">
        <div className="dropdown">
          <span>이벤트 :</span>
          <select
            value={deviceType}
            onChange={(e) => setDeviceType(e.target.value)}
          >
            {deviceOptions && deviceOptions.length > 0 ? (
              deviceOptions.map((opt) => (
                <option
                  key={opt.value}
                  value={opt.value}
                  disabled={opt.value === ""} // 빈 값은 placeholder 용도로만
                >
                  {opt.label}
                </option>
              ))
            ) : (
              // 안전장치: 비어 있으면 기본 '전체' 하나라도 노출
              <option value="all">전체</option>
            )}
          </select>
          <select
            value={subType}
            onChange={(e) => setSubType(e.target.value)}
            aria-label="세부 이벤트"
            disabled={subLoading || deviceType === "all"} // 로딩 중만 비활성화
            style={{ marginLeft: 8 }} // 필요시 CSS로 대체
          >
            {subLoading ? (
              <option value="">로딩중…</option>
            ) : subError && deviceType !== "all" ? (
              <option value="">{subError || "불러오기 실패"}</option>
            ) : (
              subOptions.map((opt) => (
                <option
                  key={opt.value}
                  value={opt.value}
                  // placeholder는 선택 불가 (all일 때는 value가 'all'이라 선택 가능)
                  disabled={opt.value === "" && deviceType !== "all"}
                >
                  {opt.label}
                </option>
              ))
            )}
          </select>
          <div className="date">
            <span>기간:</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <span>~</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* 본문 테이블 */}
      <div className="content">
        <div className="tablebox">
          <div className="info">
            총 <span>{eventTotal}</span> <span> 건</span>
          </div>

          <div className="table-container">
            <table>
              <colgroup>
                <col style={{ width: "8%" }} />
                <col style={{ width: "10%" }} />
                <col />
                <col style={{ width: "10%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "11%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>장비종류</th>
                  <th>장비이름</th>
                  <th>이벤트내용</th>
                  <th>지역</th>
                  <th>현장</th>
                  <th>이벤트시간</th>
                  <th>미디어</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center" }}>
                      불러오는 중…
                    </td>
                  </tr>
                ) : eventRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center" }}>
                      데이터가 없습니다.
                    </td>
                  </tr>
                ) : (
                  eventRows.map((r) => {
                    return (
                      <tr key={r.eventId}>
                        <td>{r.eventDeviceType}</td>
                        <td>{r.eventDeviceName}</td>
                        <td>{r.eventContent}</td>
                        <td>{r.areaName}</td>
                        <td>{r.workplaceName}</td>
                        <td>{r.eventTime}</td>
                        <td>
                          {r.downloadType
                            ? (() => {
                                const meta = getMediaMeta(r.downloadType);
                                return (
                                  <div
                                    className="align_center"
                                    onClick={() => openPopup(r)}
                                    style={{
                                      cursor: "pointer",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 6,
                                    }}
                                  >
                                    <IonIcon
                                      name={meta.icon}
                                      size={22}
                                      color={meta.color}
                                    />
                                    {meta.label}
                                  </div>
                                );
                              })()
                            : ""}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {isPopupOpen && selectedEvent && (
        <Modal
          isOpen={isPopupOpen}
          onRequestClose={closePopup}
          contentLabel="보기"
          ariaHideApp={false}
          className="pop_dimm_inner"
          overlayClassName="pop_dimm"
        >
          <div className="popup">
            <div className="popheader">
              <div className="titlebox">
                <div className="poptitle">
                  <IonIcon name="IoCall" size={24} />
                  {selectedEvent.eventDeviceType} ({selectedEvent.eventTime})
                </div>
                <button onClick={closePopup}>
                  <IonIcon name="IoClose" size={40} />
                </button>
              </div>
            </div>
            <div className="popbody">
              <div className="pop_photobox" style={{ textAlign: "center" }}>
                {(() => {
                  const kind = String(selectedEvent.downloadType).toLowerCase();
                  if (kind.includes("voice")) {
                    const src = getVoiceUrl(selectedEvent);
                    return (
                      <div className="voice-wrap">
                        {/* 파형 */}
                        <svg
                          width="180"
                          height="120"
                          viewBox="0 0 180 120"
                          aria-hidden
                          className="voice-wave"
                        >
                          {[8, 24, 40, 56, 72, 88, 104, 120, 136, 152, 168].map(
                            (x, i) => {
                              const h = [
                                22, 36, 60, 84, 100, 84, 60, 36, 22, 36, 22,
                              ][i];
                              return (
                                <rect
                                  key={x}
                                  x={x}
                                  y={(120 - h) / 2}
                                  width="8"
                                  height={h}
                                  rx="4"
                                  fill="currentColor"
                                />
                              );
                            }
                          )}
                        </svg>

                        {/* 네이티브 오디오 컨트롤 - 라운드 박스 안에 */}
                        <div className="audio-frame simple has-duration">
                          <audio
                            className="audio-ctrl native-min"
                            controls
                            preload="metadata"
                            src={src}
                            controlsList="nodownload noplaybackrate noremoteplayback"
                            onLoadedMetadata={(e) =>
                              setVoiceDuration(
                                fmtTime(e.currentTarget.duration)
                              )
                            }
                          />
                          <span className="audio-duration">
                            {voiceDuration}
                          </span>
                          <span
                            className="audio-cover"
                            aria-hidden="true"
                          ></span>
                        </div>
                      </div>
                    );
                  }

                  // // 영상
                  // if (kind.includes("video")) {
                  //   const src = getVideoUrl(selectedEvent);
                  //   return (
                  //     <video
                  //       controls
                  //       preload="metadata"
                  //       style={{
                  //         maxWidth: "100%",
                  //         maxHeight: 360,
                  //         borderRadius: 8,
                  //       }}
                  //       src={src}
                  //     />
                  //   );
                  // }

                  // // 이미지(기본)
                  // const imgSrc = getImageUrl(selectedEvent);
                  // return (
                  //   <img
                  //     src={imgSrc}
                  //     alt="파일"
                  //     style={{
                  //       maxWidth: "100%",
                  //       maxHeight: 360,
                  //       borderRadius: 8,
                  //     }}
                  //   />
                  // );
                })()}
              </div>
            </div>
            <div className="popfooter">
              <div className="bottombtns pop">
                {(() => {
                  const href = getVoiceUrl(selectedEvent); // voice 기준
                  return (
                    <a href={href} download style={{ textDecoration: "none" }}>
                      <button className="on" type="button">
                        <IonIcon name="IoCloudDownloadOutline" />
                        {getSaveLabel(selectedEvent.downloadType)}
                      </button>
                    </a>
                  );
                })()}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Event;
